import { ChainData, ChainType } from "@0xsquid/squid-types";
import { zeroAddress } from "viem";
import { useBalance, useReadContract } from "wagmi";
import { Token } from "../../core/types";
import { parseEvmAddress } from "../../services/internal/evmService";
import {
  TEMPO_FEE_MANAGER_ADDRESS,
  TempoFeeData,
  feeManagerAbi,
  isTempoChain,
} from "../../services/internal/tempoService";
import { useWallet } from "../wallet/useWallet";

const DEFAULT_REFRESH_INTERVAL_MS = 15000;

/**
 * Returns raw on-chain gas token data for Tempo chains, or null when the source
 * chain is not Tempo.
 */
export const useTempoFeeCheck = ({
  fromChain,
  fromToken,
}: {
  fromChain: ChainData | undefined;
  fromToken: Token | undefined;
}): TempoFeeData | null => {
  const { connectedAddresses } = useWallet();

  const evmAddress = parseEvmAddress(connectedAddresses[ChainType.EVM]);
  const isTempo = isTempoChain(fromChain?.chainId);
  const chainId = Number(fromChain?.chainId);

  // Read account-level gas token preference from FeeManager precompile
  // See docs: https://docs.tempo.xyz/protocol/fees/spec-fee#account-level
  const { data: accountGasTokenAddress } = useReadContract({
    address: TEMPO_FEE_MANAGER_ADDRESS,
    abi: feeManagerAbi,
    functionName: "userTokens",
    args: evmAddress ? [evmAddress] : undefined,
    chainId,
    query: {
      enabled: isTempo && !!evmAddress,
      staleTime: 30_000,
    },
  });
  const hasAccountFeePreference =
    !!accountGasTokenAddress &&
    // By default, the zero address is returned when the user has no preference set
    zeroAddress.toLowerCase() !== accountGasTokenAddress.toLowerCase();

  // Fetch balance of the account-preferred gas token (if set)
  const { data: accountGasTokenBalance } = useBalance({
    address: evmAddress,
    token: hasAccountFeePreference ? accountGasTokenAddress : undefined,
    chainId,
    query: {
      enabled: isTempo && hasAccountFeePreference && !!evmAddress,
      refetchInterval: DEFAULT_REFRESH_INTERVAL_MS,
      retry: 2,
    },
  });

  const fromTokenAddress = parseEvmAddress(fromToken?.address);

  const { data: fromTokenBalance } = useBalance({
    address: evmAddress,
    token: fromTokenAddress,
    chainId,
    query: {
      enabled: isTempo && !!evmAddress,
      refetchInterval: DEFAULT_REFRESH_INTERVAL_MS,
      retry: 2,
    },
  });

  if (!isTempo) return null;

  if (hasAccountFeePreference) {
    if (!accountGasTokenAddress || !accountGasTokenBalance) return null;

    return {
      gasTokenAddress: accountGasTokenAddress,
      gasBalance: accountGasTokenBalance.value,
    };
  }

  if (!fromTokenAddress || !fromTokenBalance) return null;

  return {
    gasTokenAddress: fromTokenAddress,
    gasBalance: fromTokenBalance.value,
  };
};
